/**
 * ROUTES constant is used to define all the routes in the application.
 * This constant is used to define the routes in a single place and use them across the application.
 *
 * How to use:
 * 1. Import ROUTES constant in any file where you want to use the routes.
 * 2. Use the routes as per the requirement.
 * 3. If you want to use the route with dynamic parameters, you can use the function to generate the route.
 *
 * Example:
 * 1. Import the ROUTES constant in the file.
 * import { ROUTES } from 'path/to/routes.constant';
 *
 * 2. Use the routes as per the requirement.
 * const loginRoute = ROUTES.AUTH.LOGIN;
 *
 * 3. Use the dynamic routes with parameters.
 * const blogDetailRoute  = ROUTES.BLOGS.DETAIL('blog-id');
 * const localDetailId = ROUTES.LOCATIONS.DETAIL.ROOT('location-id')
 * const assetDetailRoute = ROUTES.LOCATIONS.DETAIL.ASSETS.ADD('location-id', 'asset-id')
 *
 * 4. Use the dynamic routes with parameters.
 * const categoryRoute = ROUTES.CATEGORIES('category-name');
 *
 * Note: The routes are frozen to prevent any modifications to the routes object.
 * if you update base paths, make sure to update the routes accordingly. on url.helper.ts
 */

// Base path configurations
export const BASE_PATHS = {
  AUTH: 'auth',
  LOGIN: 'login',
  DASHBOARD: 'dashboard',
} as const;

export const AUTH_ROUTE_BASE = {
  LOGIN: `login`,
  CALLBACK: `callback`,
  REGISTER: `register`,
}

// Authentication-related routes
const AUTH_ROUTES = {
  LOGIN: `/${BASE_PATHS.AUTH}/${AUTH_ROUTE_BASE.LOGIN}`,
  CALLBACK: `/${BASE_PATHS.AUTH}/${AUTH_ROUTE_BASE.CALLBACK}`,
  REGISTER: `/${BASE_PATHS.AUTH}/${AUTH_ROUTE_BASE.REGISTER}`,
  SIGN_OUT: `/logout`,
} as const;

// Dashboard routes
const DASHBOARD_ROUTES = {
  OVERVIEW: `/${BASE_PATHS.DASHBOARD}/overview`,
  ASSETS: `/${BASE_PATHS.DASHBOARD}/asm`,
  TIMESHEET: `/${BASE_PATHS.DASHBOARD}/timesheet`,
  INBOX: `/${BASE_PATHS.DASHBOARD}/inbox`,
  OPERATIONS: `/${BASE_PATHS.DASHBOARD}/operations`,
  GBS: `/${BASE_PATHS.DASHBOARD}/gbs`,
  PROJECTS: `/${BASE_PATHS.DASHBOARD}/projects`,
  ANNOUNCEMENT: `/${BASE_PATHS.DASHBOARD}/announcement`,
  REPORTS: `/${BASE_PATHS.DASHBOARD}/reports`,
  ROADMAP: `/${BASE_PATHS.DASHBOARD}/roadmap`,
  CHANGELOGS: `/${BASE_PATHS.DASHBOARD}/changelogs`,
  VERSION: `/${BASE_PATHS.DASHBOARD}/version`,
  HELP: `/${BASE_PATHS.DASHBOARD}/help`,
  SETTINGS: `/${BASE_PATHS.DASHBOARD}/settings`,
  PINNED: `/${BASE_PATHS.DASHBOARD}/pinned`,
  NOTIFICATION: `/${BASE_PATHS.DASHBOARD}/notification`,
  PROFILE: `/${BASE_PATHS.DASHBOARD}/profile`,
  DEVICES: {
    ROOT: `/${BASE_PATHS.DASHBOARD}/devices`,
    LIST: `/${BASE_PATHS.DASHBOARD}/devices/list`,
    CATEGORIES: `/${BASE_PATHS.DASHBOARD}/devices/categories`,
    SUPPLIERS: `/${BASE_PATHS.DASHBOARD}/devices/suppliers`,
    LOCATIONS: `/${BASE_PATHS.DASHBOARD}/devices/locations`,
  },
  PEOPLES: `/${BASE_PATHS.DASHBOARD}/peoples`,
  PROJECT_DETAILS: {
    GENERAL: 'general',
    FINANCES: 'finances',
    MILESTONES: 'milestones',
    REPORTS: 'reports',
    TIMESHEET: 'timesheet',
  },
} as const;

// Protected routes requiring authentication
export const PROTECTED_ROUTES = {
  DASHBOARD: DASHBOARD_ROUTES,
} as const;

// Export frozen routes object
export const ROUTES = Object.freeze({
  ...PROTECTED_ROUTES,
  HOME: '/',
  BASE_PATHS,
  AUTH: AUTH_ROUTES,
});

// base route after login
export const getLoggedInUserDashboardRoute = () => ROUTES.DASHBOARD.TIMESHEET;